import { Link } from '@tanstack/react-router'
import yellowLogo from '#/assets/logo-icon-yellow.png'
import aliLogo from '#/assets/ali-logo.webp'
import footerBackground from '#/assets/footer/footer-bg.png'
import { FaFacebook, FaInstagram, FaTiktok } from 'react-icons/fa6'
import { ChevronRight } from 'lucide-react'
import { Button } from '#/components/ui/button'
import { useCourses } from '#/features/courses/hooks/useCourses'
import { useEffect, useState } from 'react'

export default function Footer() {
  const [isMounted, setIsMounted] = useState(false)
  const { data: coursesResponse } = useCourses({ limit: 6 })
  const latestCourses =
    isMounted && Array.isArray(coursesResponse?.data)
      ? coursesResponse.data.slice(0, 5)
      : []

  useEffect(() => {
    setIsMounted(true)
  }, [])

  const scrollToSection = (id: string) => {
    const el =
      document.getElementById(id) ||
      (id === 'faqs' ? document.getElementById('faq') : null)
    if (el) {
      const headerOffset = 90
      const elementPosition = el.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.scrollY - headerOffset
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
    }
  }

  return (
    <footer className="relative w-full pb-[20px] pt-[60px]" id="contact">
      <div className="absolute inset-0">
        <img
          alt=""
          className="absolute inset-0 size-full object-cover"
          src={footerBackground}
          loading="lazy"
          decoding="async"
          width={1920}
          height={800}
        />
        <div className="absolute inset-0 bg-secondary-700 mix-blend-multiply" />
      </div>

      <div className="container relative mx-auto flex flex-col gap-[30px]">
        <div className="grid w-full grid-cols-1 gap-[40px] lg:grid-cols-[339px_1fr_1fr_1fr] lg:gap-[90px]">
          <div className="flex flex-col gap-[16px]">
            <Link to="/" className="flex items-center gap-3">
              <img
                alt="Wa'allamah Institute logo"
                className="w-16 h-auto"
                src={yellowLogo}
                loading="lazy"
                decoding="async"
                width={64}
                height={64}
              />
              <h2 className="text-xl font-bold leading-[normal] text-white">
                Wa'allamah Institute
              </h2>
            </Link>
            <p className="text-[15px] font-normal leading-relaxed text-neutral-100">
              Empowering learners worldwide to connect with the Qur’an through
              authentic, personalized, and engaging Islamic education.
            </p>
            <p className="text-[14px] font-semibold text-primary-400">
              Learn the Qur’an. Live by its Guidance.
            </p>
            <div className="flex flex-col gap-1.5 text-[14px] text-white">
              <a
                href="https://wa.me/201550660071"
                target="_blank"
                rel="noreferrer"
                className="hover:text-emerald-400 transition-colors"
              >
                +20 155 066 0071
              </a>
              <a
                href="mailto:info@wa-allamah.com"
                className="hover:text-emerald-400 transition-colors"
              >
                info@wa-allamah.com
              </a>
            </div>
            <div className="flex items-start text-white gap-3 text-3xl">
              <a
                className="hover:text-sky-500 transition-all"
                href="https://www.facebook.com/share/198Y8kAYwL/"
                target="_blank"
                rel="noreferrer"
                aria-label="Wa'allamah Institute on Facebook"
              >
                <FaFacebook />
              </a>
              <a
                className="hover:text-pink-500 transition-all"
                href="https://www.instagram.com/waallamahinstitute"
                target="_blank"
                rel="noreferrer"
                aria-label="Wa'allamah Institute on Instagram"
              >
                <FaInstagram />
              </a>
              <a
                className="hover:text-neutral-300 transition-all"
                href="https://www.tiktok.com/@waallamahacademy"
                target="_blank"
                rel="noreferrer"
                aria-label="Wa'allamah Institute on TikTok"
              >
                <FaTiktok />
              </a>
            </div>
          </div>

          <div className="flex flex-col gap-[30px] text-white">
            <h2 className="text-xl font-bold leading-[normal] text-primary-500">
              Courses
            </h2>
            <div className="flex flex-col gap-[8px] text-[16px]">
              {latestCourses.length > 0 ? (
                latestCourses.map((course) => (
                  <Link
                    key={course.id}
                    to="/courses/$courseId"
                    params={{ courseId: course.slug || course.id }}
                    className="hover:text-primary-200 transition-colors line-clamp-1"
                  >
                    {course.title}
                  </Link>
                ))
              ) : (
                <>
                  <Link
                    to="/courses"
                    className="hover:text-primary-200 transition-colors"
                  >
                    Online Arabic Grammar Course
                  </Link>
                  <Link
                    to="/courses"
                    className="hover:text-primary-200 transition-colors"
                  >
                    Online Arabic Classes for Kids
                  </Link>
                  <Link
                    to="/courses"
                    className="hover:text-primary-200 transition-colors"
                  >
                    Online Quran Tafseer Course
                  </Link>
                  <Link
                    to="/courses"
                    className="hover:text-primary-200 transition-colors"
                  >
                    Online Quranic Arabic Course
                  </Link>
                  <Link
                    to="/courses"
                    className="hover:text-primary-200 transition-colors"
                  >
                    Online Qirat Course
                  </Link>
                </>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-[30px] text-white">
            <h2 className="text-xl font-bold leading-[normal] text-primary-500">
              Links
            </h2>
            <div className="flex flex-col gap-[8px] text-[16px]">
              <Link
                to="/about"
                hash="teachers"
                onClick={() => scrollToSection('teachers')}
                className="hover:text-primary-200 transition-colors"
              >
                Teachers
              </Link>
              <Link
                to="/about"
                hash="testimonials"
                onClick={() => scrollToSection('testimonials')}
                className="hover:text-primary-200 transition-colors"
              >
                Testimonials
              </Link>
              <Link
                to="/about"
                hash="faqs"
                onClick={() => scrollToSection('faqs')}
                className="hover:text-primary-200 transition-colors"
              >
                FAQs
              </Link>

              <Link
                to="/plans"
                className="hover:text-primary-200 transition-colors"
              >
                Plans
              </Link>
              <Link
                to="/blog"
                className="hover:text-primary-200 transition-colors"
              >
                Blog
              </Link>
              <Link
                to="/contact"
                className="hover:text-primary-200 transition-colors"
              >
                Contact Us
              </Link>
            </div>
          </div>

          <div className="flex flex-col gap-[30px] text-white">
            <h2 className="text-xl font-bold leading-[normal] text-primary-500">
              Join Our Academy
            </h2>
            <p className="text-[16px] font-normal leading-[normal] text-neutral-50">
              Book a free trial assessment class with an Ijaza-certified tutor
              today.
            </p>
            <Button asChild size="lg" className="w-fit">
              <Link to="/subscribe">
                Enroll for Free Trial <ChevronRight />
              </Link>
            </Button>
          </div>
        </div>

        <div className="flex w-full flex-col items-center justify-center gap-1.5 border-t border-secondary-500 py-[20px] text-[14px] text-neutral-300 text-center">
          <p>
            © {new Date().getFullYear()} Wa'allamah Institute. All rights
            reserved.
          </p>
          <div className="flex items-center justify-center gap-2 text-neutral-300">
            <span>Made with passion by</span>
            <a
              href="https://www.linkedin.com/in/ali-alaa-eldin/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center transition-transform hover:scale-105"
              aria-label="Ali Alaa Eldin LinkedIn Profile"
            >
              <img
                src={aliLogo}
                alt="Ali Alaa Eldin"
                className="h-10 w-auto object-contain"
                loading="lazy"
                decoding="async"
                width={40}
                height={40}
              />
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}
